<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, FormField, PageHeader, Section, Select } from '@drama/ui'
import { listStoryAnimatedCovers, setStoryAnimatedCoverEnabled, uploadStoryAnimatedCover } from '../api/novel/animated-covers.js'

const { t, locale } = useI18n()
const text = (key, params = {}) => t(`visualGameAdmin.storyAnimatedCovers.${key}`, params)
const stories = ref([])
const covers = ref([])
const selected_slug = ref('')
const file = ref(null)
const preview_url = ref('')
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const notice = ref('')
const input = ref(null)
const local_preview = import.meta.env.DEV && import.meta.env.VITE_STORY_COVER_PREVIEW === 'true'
const standalone = location.pathname === '/story-covers.html'
const player_url = ref(local_preview ? `${location.protocol}//${location.hostname}:5182/` : '')
const options = computed(() => stories.value.map(story => ({ value: story.slug, label: story.title })))
const selected_story = computed(() => stories.value.find(story => story.slug === selected_slug.value))
const rows = computed(() => covers.value.map(cover => ({ ...cover, story: stories.value.find(story => story.slug === cover.story_slug) })))
function failure(code) { return new Error(code) }
async function request(path, options = {}) {
  const response = await fetch('/__story_cover_preview' + path, { cache: 'no-store', ...options })
  const result = await response.json()
  if (!response.ok) {
    const code = ['invalid_mp4', 'invalid_ratio', 'silent_mp4_required', 'mp4_too_large'].includes(result.error) ? result.error : 'request_failed'
    throw failure(code)
  }
  return result
}
const cover_api = local_preview ? {
  list: () => request('/covers'),
  upload: (slug, chosen) => request('/covers/' + encodeURIComponent(slug), { method: 'PUT', headers: { 'content-type': 'video/mp4' }, body: chosen }),
  toggle: (slug, enabled) => request('/covers/' + encodeURIComponent(slug), { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ enabled }) }),
} : { list: listStoryAnimatedCovers, upload: uploadStoryAnimatedCover, toggle: setStoryAnimatedCoverEnabled }
function errorMessage(failure) {
  const code = ['invalid_mp4', 'invalid_ratio', 'silent_mp4_required', 'mp4_too_large'].find(code => failure.message === code)
  return text(`errors.${code || 'request_failed'}`)
}
async function load() {
  loading.value = true
  error.value = ''
  try {
    const settings = await cover_api.list()
    covers.value = settings.items
    if (!local_preview) player_url.value = settings.home_url || ''
    const found = []
    let cursor = ''
    do {
      const query = new URLSearchParams({ game_type: 'story', locale: locale.value, limit: '50', ...(cursor ? { cursor } : {}) })
      const response = await fetch('/api/visual-game/catalog?' + query)
      if (!response.ok) throw failure('request_failed')
      const result = await response.json()
      found.push(...result.items)
      cursor = result.has_more ? result.next_cursor : ''
    } while (cursor)
    stories.value = found
    selected_slug.value ||= covers.value[0]?.story_slug || found[0]?.slug || ''
  } catch (failure) { error.value = errorMessage(failure) }
  finally { loading.value = false }
}
function clearFile() {
  if (preview_url.value) URL.revokeObjectURL(preview_url.value)
  preview_url.value = ''
  file.value = null
  if (input.value) input.value.value = ''
}
async function choose(event) {
  const chosen = event.target.files?.[0]
  clearFile()
  error.value = ''
  notice.value = ''
  if (!chosen) return
  if (chosen.size > 3 * 1024 * 1024) { error.value = text('errors.mp4_too_large'); return }
  const signature = new TextDecoder().decode(await chosen.slice(4, 8).arrayBuffer())
  if (signature !== 'ftyp') { error.value = text('errors.invalid_mp4'); return }
  file.value = chosen
  preview_url.value = URL.createObjectURL(chosen)
}
async function upload() {
  if (!file.value || !selected_slug.value || saving.value) return
  saving.value = true
  error.value = ''
  notice.value = ''
  try {
    await cover_api.upload(selected_slug.value, file.value)
    clearFile()
    await load()
    notice.value = text('saved')
  } catch (failure) { error.value = errorMessage(failure) }
  finally { saving.value = false }
}
async function toggle(cover) {
  if (saving.value) return
  saving.value = true
  error.value = ''
  notice.value = ''
  try {
    await cover_api.toggle(cover.story_slug, !cover.enabled)
    await load()
    notice.value = text('saved')
  } catch (failure) { error.value = errorMessage(failure) }
  finally { saving.value = false }
}
watch(locale, load)
onMounted(load)
onBeforeUnmount(clearFile)
</script>

<template>
  <main class="vga-page story-cover-settings">
    <PageHeader :title="text('title')">
      <template #actions>
        <a
          v-if="standalone"
          class="story-cover-settings__link"
          href="/story-animated-covers"
        >{{ t('visualGameAdmin.app.name') }}</a>
        <a
          v-if="player_url"
          class="story-cover-settings__link"
          :href="player_url"
          target="_blank"
          rel="noopener"
        >{{ text('openHome') }}</a><Button
          variant="secondary"
          :loading="loading"
          :disabled="saving"
          @click="load"
        >
          {{ text('refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p
      v-if="error"
      class="vga-error"
      role="alert"
    >
      {{ error }}
    </p>
    <p
      v-if="notice"
      role="status"
    >
      {{ notice }}
    </p>
    <Section :title="text('uploadTitle')">
      <p>{{ text(local_preview ? 'localSubtitle' : 'subtitle') }}</p>
      <div class="story-cover-settings__form">
        <FormField
          :label="text('story')"
          for="story-cover-story"
        >
          <Select
            id="story-cover-story"
            v-model="selected_slug"
            :aria-label="text('story')"
            :options="options"
            :disabled="loading || saving"
            block
          />
        </FormField>
        <FormField
          :label="text('file')"
          :hint="text('fileHint')"
          for="story-cover-file"
        >
          <input
            id="story-cover-file"
            ref="input"
            type="file"
            accept="video/mp4,.mp4"
            :disabled="saving"
            data-testid="story-cover-file"
            @change="choose"
          >
        </FormField>
        <div
          v-if="preview_url"
          class="story-cover-settings__preview"
        >
          <video
            :src="preview_url"
            :aria-label="selected_story?.title || ''"
            autoplay
            muted
            loop
            playsinline
            @error="error = text('errors.invalid_mp4')"
          /><span>{{ Math.round(file.size / 1000) }} KB</span>
        </div>
        <Button
          :loading="saving"
          :disabled="!file || !selected_slug || loading"
          data-testid="story-cover-upload"
          @click="upload"
        >
          {{ text('save') }}
        </Button>
      </div>
    </Section>
    <Section :title="text('currentTitle')">
      <p v-if="!rows.length && !loading">
        {{ text('empty') }}
      </p>
      <article
        v-for="cover in rows"
        :key="cover.story_slug"
        class="story-cover-settings__row"
        :data-testid="`story-cover-${cover.story_slug}`"
      >
        <video
          :src="cover.url"
          :aria-label="cover.story?.title || cover.story_slug"
          class="story-cover-settings__image"
          autoplay
          muted
          loop
          playsinline
        />
        <div class="story-cover-settings__identity">
          <strong>{{ cover.story?.title || cover.story_slug }}</strong><span>{{ cover.width }} × {{ cover.height }} · {{ (cover.duration_ms / 1000).toFixed(1) }} s · {{ Math.round(cover.size_bytes / 1000) }} KB</span><Badge :variant="cover.enabled ? 'success' : 'neutral'">
            {{ text(cover.enabled ? 'enabled' : 'disabled') }}
          </Badge>
        </div>
        <Button
          :disabled="saving"
          :variant="cover.enabled ? 'secondary' : 'primary'"
          data-testid="story-cover-toggle"
          @click="toggle(cover)"
        >
          {{ text(cover.enabled ? 'disable' : 'enable') }}
        </Button>
      </article>
    </Section>
  </main>
</template>

<style scoped>
.story-cover-settings { display: grid; max-width: 1080px; margin: 0 auto; padding: var(--drama-space-2xl); gap: var(--drama-space-xl); }
.story-cover-settings__link { color: var(--drama-color-primary); text-decoration: underline; }
.story-cover-settings__form { display: grid; max-width: 560px; gap: var(--drama-space-lg); }
.story-cover-settings__form input { max-width: 100%; color: var(--drama-color-text); }
.story-cover-settings__form > button { justify-self: start; }
.story-cover-settings__preview { display: grid; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.story-cover-settings__preview video, .story-cover-settings__image { display: block; width: 150px; max-width: 100%; aspect-ratio: 9 / 16; object-fit: cover; border-radius: var(--drama-radius-md); }
.story-cover-settings__row { display: flex; align-items: center; gap: var(--drama-space-xl); padding: var(--drama-space-lg) 0; }
.story-cover-settings__identity { display: grid; flex: 1; justify-items: start; gap: var(--drama-space-sm); }
.story-cover-settings__identity > span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
@media (max-width: 680px) { .story-cover-settings { padding: var(--drama-space-lg); } .story-cover-settings__row { flex-wrap: wrap; gap: var(--drama-space-md); } .story-cover-settings__image { width: 108px; } .story-cover-settings__identity { min-width: 150px; } }
</style>
